import { 
  Building, 
  Calendar, 
  DollarSign, 
  MapPin, 
  Clock, 
  FileText,
  Shield,
  ExternalLink
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';

interface RfqCardProps {
  rfq: {
    id: string;
    title: string;
    agency: string;
    location: string;
    budget: string;
    deadline: string;
    description: string;
    rfqNumber: string;
    setAsideProgram?: string;
    postedDate: string;
    responseDeadline: string;
    category: string;
    // isGovCertified: boolean;
    // govStatus: string;
  };
  onViewDetails?: (rfqId: string) => void;
}

export default function RfqCard({ rfq, onViewDetails }: RfqCardProps) {
  const { user } = useAuthStore();
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysUntilDeadline = (deadline: string) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysLeft = getDaysUntilDeadline(rfq.responseDeadline);
  const isUrgent = daysLeft <= 7;
  
  // Check if user can submit proposals (same logic as RfqDetailModal)
  const isEnterpriseVendor = (user?.role === 'VENDOR' && user?.isEnterprise === true) || user?.role === 'ENTERPRISE_VENDOR';
  const canRespond = isEnterpriseVendor && user?.isGovCertified === true;

  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 hover:shadow-lg transition-shadow duration-200">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {rfq.title}
            </h3>
            <div className="flex items-center text-sm text-gray-600 mb-2">
              <Building className="h-4 w-4 mr-1" />
              <span>{rfq.agency}</span>
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                {rfq.rfqNumber}
              </span>
            </div>
          </div>
          
          {rfq.setAsideProgram && (
            <div className="ml-4">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                <Shield className="h-3 w-3 mr-1" />
                {rfq.setAsideProgram}
              </span>
            </div>
          )}
        </div>

        {/* Description */}
        <p className="text-gray-700 text-sm mb-4 line-clamp-3">
          {rfq.description}
        </p>

        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="flex items-center text-sm">
            <DollarSign className="h-4 w-4 text-green-500 mr-2" />
            <div>
              <p className="text-gray-600">Budget</p>
              <p className="font-medium text-gray-900">{rfq.budget}</p>
            </div>
          </div>
          
          <div className="flex items-center text-sm">
            <MapPin className="h-4 w-4 text-blue-500 mr-2" />
            <div>
              <p className="text-gray-600">Location</p>
              <p className="font-medium text-gray-900">{rfq.location}</p>
            </div>
          </div>
          
          <div className="flex items-center text-sm">
            <Calendar className="h-4 w-4 text-purple-500 mr-2" />
            <div>
              <p className="text-gray-600">Posted</p>
              <p className="font-medium text-gray-900">{formatDate(rfq.postedDate)}</p>
            </div>
          </div>
          
          <div className="flex items-center text-sm">
            <Clock className={`h-4 w-4 mr-2 ${isUrgent ? 'text-red-500' : 'text-orange-500'}`} />
            <div>
              <p className="text-gray-600">Deadline</p>
              <p className={`font-medium ${isUrgent ? 'text-red-600' : 'text-gray-900'}`}>
                {formatDate(rfq.responseDeadline)}
              </p>
              {daysLeft > 0 && (
                <p className={`text-xs ${isUrgent ? 'text-red-500' : 'text-gray-500'}`}>
                  {daysLeft} days left
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Category */}
        <div className="mb-4">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <FileText className="h-3 w-3 mr-1" />
            {rfq.category}
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <button 
            className="flex items-center text-sm text-blue-600 hover:text-blue-800 transition-colors"
            onClick={() => {
              if (onViewDetails) {
                onViewDetails(rfq.id);
              }
            }}
          >
            <ExternalLink className="h-4 w-4 mr-1" />
            View Details
          </button>
          
          {canRespond ? (
            <button 
              onClick={() => {
                if (onViewDetails) {
                  onViewDetails(rfq.id);
                }
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              Submit Proposal
            </button>
          ) : (
            <button 
              disabled
              className="px-4 py-2 bg-gray-300 text-gray-500 rounded-md text-sm font-medium cursor-not-allowed"
              title={
                !user 
                  ? "Please log in to respond"
                  : !isEnterpriseVendor 
                    ? "Enterprise vendor account required"
                    : "Government certification required"
              }
            >
              Submit Proposal
            </button>
          )}
        </div>
      </div>
    </div>
  );
} 